import React, {useRef} from 'react';
import styled from 'styled-components';
import {useTranslation} from 'react-i18next';
import {Button, List} from '@tickchak/react-share/app';
import useShow from '../../../../../hooks/useShow';
import {getMenuPosition} from '../../../utils';
import useCloseOnScroll from '../../../../../hooks/useCloseOnScroll';
import {TLecturer} from '../../../../../redux/timetable/timetable.type';
import {bounceMessage} from '../../../../../layout/userMessages/UserMessage';
import KebabDots from '../../../../../assets/icons/KebabDots';
import EditIcon from '../../../../../assets/icons/EditIcon';
import TrashIcon from '../../../../../assets/icons/TrashIcon';
import XIcon from '../../../../../assets/icons/XIcon';

const TriggerButton = styled(Button)`
    &:focus, &:focus-visible { outline: none; box-shadow: none; }
`;

interface LecturerCardMenuProps {
    lecturer: TLecturer;
    onEdit?: () => void;
    onDelete?: () => void;
    onRemoveFromConference?: () => void;
    isAssignedToLecture?: boolean;
}
const DROPDOWN_WIDTH = 278;

function LecturerCardMenu({lecturer, onEdit = () => {}, onDelete = () => {}, onRemoveFromConference = () => {}, isAssignedToLecture}: LecturerCardMenuProps) {
    const {t} = useTranslation('timetable');
    const show = useShow();
    const triggerRef = useRef<HTMLSpanElement>(null);
    useCloseOnScroll(show.isOpen, show.close);

    const toggle = (e: React.MouseEvent) => {
        e.stopPropagation();
        show.isOpen ? show.close() : show.open();
    };

    const handleItemClick = ({id}: {id: string | number}) => {
        if(id === 'edit') onEdit();
        else if(id === 'removeFromConference') onRemoveFromConference();
        else if(id === 'delete') {
            bounceMessage(
                t('confirmDeleteLecturerMessage'),
                'attention',
                {text: t('deleteLecturer'), onClick: onDelete},
                {text: t('cancel')}
            );
        }
        show.close();
    };

    return (
        <>
            <span ref={triggerRef}>
                <TriggerButton
                    variant="subtle"
                    size="chip"
                    iconStart={<KebabDots />}
                    ariaLabel={t('lecturerMenu')}
                    ariaHaspopup="menu"
                    ariaExpanded={show.isOpen}
                    onClick={toggle}
                    data-cy={`lecturer-card-${lecturer.lid}-menu`}
                    customStyle={{width: 14, height: 14, padding: 0, minWidth: 0, opacity: 0.6}}
                />
            </span>
            {show.isOpen && (
                <List
                    fixedPosition={getMenuPosition(triggerRef)}
                    show={show}
                    onItemClick={handleItemClick}
                    position={{width: `${DROPDOWN_WIDTH}px`, height: 'fit-content', minHeight: 'fit-content'}}
                >
                    <List.Items>
                        <List.Item.Item id="delete" text={t('deleteLecturer')} startIcon={<TrashIcon />} />
                        <List.Item.Item id="edit"   text={t('editLecturer')}   startIcon={<EditIcon />} />
                        {!isAssignedToLecture &&
                            <List.Item.Item id="removeFromConference" text={t('removeFromConference')} startIcon={<XIcon />} />}
                    </List.Items>
                    <List.Footer fixed>
                        <List.Footer.Close text={t('close')} size="category" />
                    </List.Footer>
                </List>
            )}
        </>
    );
}

export default LecturerCardMenu;
